import { z } from 'zod'
import { MaterialArea, MaterialStatus, RDrugProduct, RDrugIcd, UserRole } from '@fusion-advantage/backcompat'
import { AppEnvironment, RAppointment, RDashboardNotification, RTodo, UserIdentities } from '@fusion-advantage/formlib'
import _ from 'lodash'
import { XFrontendRequired } from '../../lib/XFrontend'

//
//
//  APP STATUS
//

const ZCloudToDeviceAppStatusBodyUser = z.object({
	UPN: z.string(),
	Username: z.string(),
	FullName: z.string(),
	Roles: z.array(z.enum(UserRole)),
})
export const ZCloudToDeviceAppStatusBody = z.object({
	Authenticated: z.boolean(),
	AuthedIdentity: z.nullable(ZCloudToDeviceAppStatusBodyUser),
	ImpersonatingIdentity: z.nullable(ZCloudToDeviceAppStatusBodyUser),
	SessionExpiredAt: z.nullable(z.number()),
	Environment: z.enum(AppEnvironment),
	EtagTestHash: z.string(),
})
export type CloudToDeviceAppStatusBody = z.infer<typeof ZCloudToDeviceAppStatusBody>
export type CloudToDeviceAppStatusOk = CloudToDeviceAppStatusBody & {
	FrontendRequired: XFrontendRequired|null
}

export const appStatusBodyToUserIdentities = (body: CloudToDeviceAppStatusBody): UserIdentities => {
	return {
		authedIdentity: body.AuthedIdentity ? {
			upn: body.AuthedIdentity.UPN,
			username: body.AuthedIdentity.Username,
			fullName: body.AuthedIdentity.FullName,
			roles: body.AuthedIdentity.Roles,
		} : null,
		impersonatingIdentity: body.ImpersonatingIdentity ? {
			upn: body.ImpersonatingIdentity.UPN,
			username: body.ImpersonatingIdentity.Username,
			fullName: body.ImpersonatingIdentity.FullName,
			roles: body.ImpersonatingIdentity.Roles,
		} : null,
	}
}

//
//
//  OFFLINE TABLE
//

export const ZCloudToDeviceOfflineTableDashboardNotificationBody = z.object({
	DashboardNotifications: z.array(RDashboardNotification),
})
export type CloudToDeviceOfflineTableDashboardNotificationsBody = z.infer<typeof ZCloudToDeviceOfflineTableDashboardNotificationBody>

export const ZCloudToDeviceOfflineTableDrugProductBody = z.object({
	Products: z.array(RDrugProduct),
})
export type CloudToDeviceOfflineTableDrugProductBody = z.infer<typeof ZCloudToDeviceOfflineTableDrugProductBody>

export const ZCloudToDeviceOfflineTableDrugProductMapBody = z.object({
	ProductMaps: z.array(z.object({
		ProductID: z.number(),
		Icd10Code: z.string(),
	})),
})
export type CloudToDeviceOfflineTableDrugProductMapBody = z.infer<typeof ZCloudToDeviceOfflineTableDrugProductMapBody>

export const ZCloudToDeviceOfflineTableDrugSearchWordBody = z.object({
	Words: z.array(z.string()),
})
export type CloudToDeviceOfflineTableDrugSearchWordBody = z.infer<typeof ZCloudToDeviceOfflineTableDrugSearchWordBody>

export const ZCloudToDeviceOfflineTableDrugIcdBody = z.object({
	Codes: z.array(RDrugIcd),
})
export type CloudToDeviceOfflineTableDrugIcdBody = z.infer<typeof ZCloudToDeviceOfflineTableDrugIcdBody>

export const ZCloudToDeviceOfflineTableMaterialInventoryBody = z.object({
	Materials: z.array(z.object({
		Area: z.enum(MaterialArea),
		Id: z.string(),
		Type: z.string(),
		Status: z.enum(MaterialStatus),
	}))
})
export type CloudToDeviceOfflineTableMaterialInventoryBody = z.infer<typeof ZCloudToDeviceOfflineTableMaterialInventoryBody>

//
//
//  WORK ITEMS
//

export const ZCloudToDeviceTodoBody = z.object({
	WorkItems: z.array(RTodo),
})
export type CloudToDeviceTodoBody = z.infer<typeof ZCloudToDeviceTodoBody>

export const ZCloudToDeviceApptBody = z.object({
	Appointments: z.array(RAppointment),
	FilterByDate: z.boolean(),
	StartDate: z.nullable(z.iso.date()),
	EndDate: z.nullable(z.iso.date()),
})
export type CloudToDeviceApptBody = z.infer<typeof ZCloudToDeviceApptBody>

//
//
//  EXPORT
//

export type CloudToDeviceExportArgs = {
	EncounterGUID: string
	DeviceToCloudCodeNext?: string
} | {
	EncounterGUID?: never
	DeviceToCloudCodeNext?: never
	MemberGUID: string
	SfSvcApptId: string
	Latitude: number|null
	Longitude: number|null
	Branch_Designator: string|null
}

//
//
//  FILE
//

export const ZCloudToDeviceDownloadFileBody = z.object({
	SasUrl: z.string()
})
export type CloudToDeviceDownloadFileBody = z.infer<typeof ZCloudToDeviceDownloadFileBody>
